import React, { useEffect, useState, useRef } from "react";
import { supabase } from '../../supabase_client.jsx';
import "./AddEvent.css";

export default function AddEvent( {onViewSelect, returnView ='business', currentUser }) {

    const [eventOwner, setEventOwner] = useState('');
    const [businessEventTypes, setBusinessEventTypes] = useState([]);

    useEffect(() => {
        async function fetchBusinessInfo() {
            const userId = currentUser?.user?.id;
            if (!userId) return;

            // Fetch username
            const { data: userData } = await supabase
                .from('users')
                .select('username')
                .eq('user_id', userId)
                .single();
            if (userData) setEventOwner(userData.username);

            // Fetch business event_types from the business profile
            const { data: bizData } = await supabase
                .from('businesses')
                .select('event_types')
                .eq('user_id', userId)
                .single();
            if (bizData?.event_types) setBusinessEventTypes(bizData.event_types);
        }
        fetchBusinessInfo();
    }, [currentUser]);

    console.log("AddEvent currentUser:", currentUser);
    const [name, setName] = useState(null);
    const [business, setBusiness] = useState(null);
    const [description, setDescription] = useState(null);
    const [location, setLocation] = useState(null);
    const [date, setDate] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // photo upload state
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const fileInputRef = useRef(null);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (!file || !file.type.startsWith("image/")) return;
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    async function SaveNewEvent() {
        setIsSubmitting(true);
        try {
            // 1. Validate date is not in the past
            if (!date) {
                alert("Please select a date for your event.");
                setIsSubmitting(false);
                return;
            }

            // Split YYYY-MM-DD to avoid timezone shifting issues
            const [year, month, day] = date.split('-').map(Number);
            const selectedDate = new Date(year, month - 1, day);
            
            const today = new Date();
            today.setHours(0, 0, 0, 0); 

            if (selectedDate < today) {
                alert("Event date cannot be in the past. Please select today or a future date.");
                setIsSubmitting(false);
                return;
            }

            // 2. Validate other required fields
            if (!name || !location || !startTime) {
                alert("Please fill in the Name, Location, and Start Time.");
                setIsSubmitting(false);
                return;
            }

            // 3. Handle Photo Upload (Original logic moved here)
            let uploadedImageUrl = null;
            if (photoFile) {
                const fileExt = photoFile.name.split('.').pop();
                const fileName = `${Math.random()}.${fileExt}`;
                const filePath = `event-photos/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('events')
                    .upload(filePath, photoFile);

                if (uploadError) {
                    throw uploadError;
                }

                const { data: urlData } = supabase.storage
                    .from('events')
                    .getPublicUrl(filePath);

                uploadedImageUrl = urlData.publicUrl;
            }

            // 4. Save to Database
            const { data: insertedEvent, error } = await supabase
                .from('events')
                .insert([{
                    name,
                    description,
                    location,
                    date,
                    start_time: startTime,
                    end_time: endTime,
                    image_url: uploadedImageUrl,
                    event_owner: eventOwner,
                    is_approved: false
                }])
                .select()
                .single();

            if (error) throw error;

            // 5. Save business event_types as categories for this event
            if (insertedEvent && businessEventTypes.length > 0) {
                const categoryRows = businessEventTypes.map((cat) => ({
                    event_id: insertedEvent.id,
                    category: cat,
                }));
                const { error: catError } = await supabase
                    .from('event_category')
                    .insert(categoryRows);
                if (catError) console.error('Error saving event categories:', catError.message);
            }

            alert("Event successfully submitted and pending admin review!");
            onViewSelect(returnView);
        } catch (error) {
            console.error('Error saving event:', error.message);
            alert("Error saving event: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <div className="add-event-page">
            {/* header */}
            <div className="calendar-top-nav">
                <div className="nav-left">
                    <button className="nav-tab" onClick={() => onViewSelect(returnView)}>
                        ← Back
                    </button>
                </div>
            </div>

            <div className="add-event-card">
                {/** header */}
                <div className="add-event-card-header">
                    <div>
                        <h2 className="add-event-title">New Event</h2>
                        <span className="add-event-subtitle">Fill in the details below to submit your event for approval.</span>           
                    </div>
                </div>

                {/** body */}
                <div className="add-event-body">

                    {/* Left column */}
                    <div className="add-event-left">
                        {/* Photo upload box */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handlePhotoChange}
                        />
                        <div
                            className="add-event-image-box"
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            style={{ cursor: "pointer", overflow: "hidden", position: "relative" }}
                        >
                            {photoPreview ? (
                                <>
                                    <img
                                        src={photoPreview}
                                        alt="Event preview"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                        }}
                                    />
                                    <div className="image-box-overlay">
                                        <span>Change Photo</span>
                                    </div>
                                </>
                            ) : (
                                <div className="image-box-placeholder">
                                    <div className="image-box-icon">📷</div>
                                    <span>Click or drag to upload photo</span>
                                </div>
                            )}
                        </div>

                        {/* Meta grid */}
                        <div className="add-event-meta-grid">
                            <div className="admin-field">
                                <label>Date</label>
                                <input
                                    type="date"
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                            <div className="admin-field">
                                <label>Start Time</label>
                                <input
                                    type="time"
                                    onChange={(e) => setStartTime(e.target.value)}
                                />
                            </div>
                            <div className="admin-field">
                                <label>End Time</label>
                                <input
                                    type="time"
                                    onChange={(e) => setEndTime(e.target.value)}
                                />
                            </div>
                            <div className="admin-field">
                                <label>Location</label>
                                <input
                                    type="text"
                                    placeholder="e.g. 123 Main St"
                                    onChange={(e) => setLocation(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right column */}
                    <div className="add-event-right">
                        <div className="admin-field">
                            <label>Event Name</label>
                            <input
                                type="text"
                                placeholder="Enter event name"
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        {/** QUESTION: events should show the business that is hosting? */}
                        {/* <div className="admin-field">
                            <label>Business Name</label>
                            <input
                                type="text"
                                placeholder="Enter business name"
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div> */}
                        <div className="admin-field">
                            <label>Event Owner</label>
                            <input
                                type="text"
                                value={eventOwner || ''}
                                readOnly
                                style={{ background: '#f0f0f0', color: '#666' }}
                            />
                        </div>
                        <div className="admin-field" style={{ flex: 1 }}>
                            <label>Description</label>
                            <textarea
                                placeholder="Describe your event..."
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="admin-modal-footer">
                    <button
                        className="admin-cancel-btn"
                        type="button"
                        onClick={() => onViewSelect(returnView)}
                    >
                        Cancel
                    </button>
                    <button
                        className="admin-approve-btn"
                        type="button"
                        onClick={SaveNewEvent}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Submitting…" : "Submit Event"}
                    </button>
                </div>
            </div>
        </div>
    );
}